import { Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "./firebase-config/firebase";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fonts, colors } from "../ColorFont/color";
import { url } from "../ConstUrl/Url";
import { useUser } from "../context/UserContext";
import ProfileScreen from "../screens/ProfileScreen";
import { profileTranslations } from "../constants/profileTranslations";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refreshUserData } = useUser();

  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    gender: "",
    age: "",
    country: "India",
    state: "",
    tahsil: "",
    role: location.search.split("=")[1] || "",
    email: "",
    phoneNumber: "",
    location: "",
    district: "",
    village: "",
  });

  // Check if role is provided from URL (user selected "Yes" and chose role)
  const isRoleFromURL = location.search.split("=")[1] ? true : false;

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const isPhoneLogin = user.providerData.some(
        (provider) => provider.providerId === "phone"
      );

      setUserDetails({
        username: user.displayName || "User",
        email: user.email || "",
        phoneNumber: isPhoneLogin ? user.phoneNumber : "",
        photoURL: user.photoURL,
      });

      setFormData((prevData) => ({
        ...prevData,
        email: user.email || "",
        phoneNumber: isPhoneLogin ? user.phoneNumber : "",
      }));
    }
  }, []);

  const handleSignOut = async () => {
    // Left unchanged for reference, but currently not used in Screen
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Remove non-digits and cap at 10 for mobile
    if (name === "phoneNumber") {
      value = value.replace(/\D/g, "");
      if (value.length > 10) value = value.slice(0, 10);
    }

    // Allow only alphabetical characters and spaces for names
    if (name === "firstName" || name === "lastName") {
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setFormData((prevData) => {
      const updated = { ...prevData, [name]: value };
      if (name === "state") {
        updated.district = "";
      }
      return updated;
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const tTrans = profileTranslations[i18n.language] || profileTranslations['en'];

    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = tTrans.error_firstName;
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = tTrans.error_lastName;
    }

    if (!formData.gender) {
      newErrors.gender = tTrans.error_gender;
    }

    if (!formData.age || isNaN(formData.age) || formData.age <= 0 || formData.age > 120) {
      newErrors.age = tTrans.error_age;
    }

    if (!formData.phoneNumber || formData.phoneNumber.length < 10) {
      newErrors.phoneNumber = tTrans.error_mobile;
    }

    if (!formData.state) {
      newErrors.state = tTrans.error_state;
    }

    if (!formData.district) {
      newErrors.district = tTrans.error_district;
    }

    if (!formData.tahsil) {
      newErrors.tahsil = tTrans.error_taluka;
    }

    if (!formData.village) {
      newErrors.village = tTrans.error_village;
    }

    if (!isRoleFromURL && !formData.role) {
      newErrors.role = tTrans.error_role;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(`${url}/api/user-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Step 2: Sync newly created profile with context
        const userIdentifier = formData.email || formData.phoneNumber || userDetails?.email || userDetails?.phoneNumber;
        await refreshUserData(userIdentifier);

        const accountName = formData.fullName || `${formData.firstName} ${formData.lastName}`.trim();
        const createdRole = formData.role || location.state?.role;

        setFormData({
          firstName: "",
          lastName: "",
          gender: "",
          country: "India",
          state: "",
          tahsil: "",
          age: "",
          role: location.state?.role,
          email: userDetails?.email || "",
          phoneNumber: userDetails?.phoneNumber || "",
        });

        if (createdRole === 'Seller') {
          navigate("/farmer-success", { state: { userName: accountName } });
        } else if (createdRole === 'Buyer') {
          navigate("/buyer-success", { state: { userName: accountName } });
        } else {
          navigate("/home");
        }
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  if (!userDetails) {
    return (
      <Typography
        variant="h6"
        textAlign="center"
        sx={{
          marginTop: "20px",
          fontFamily: fonts.poppins,
          color: colors.primary_dark,
        }}
      >
        Loading...
      </Typography>
    );
  }

  return (
    <ProfileScreen
      formData={formData}
      onChange={handleInputChange}
      onSubmit={handleFormSubmit}
      errors={errors}
      onBack={() => navigate(-1)}
      lang={i18n.language}
      onLangChange={(l) => i18n.changeLanguage(l)}
      role={formData.role}
    />
  );
};

export default Profile;